import apiClient from '../core';
import type {
  Page,
  CreatePageRequest,
  UpdatePageRequest,
  CreatePageTemplateRequest,
  CreatePageFromTemplateRequest,
  CreatePageFromTemplateResponse,
} from '../../schemas/validation';

export const pagesApi = {
  async create(siteId: string, data: CreatePageRequest): Promise<Page> {
    const response = await apiClient.post<Page>(`/sites/${siteId}/pages`, data);
    return response.data;
  },

  async get(pageId: string): Promise<Page> {
    const response = await apiClient.get<Page>(`/pages/${pageId}`);
    return response.data;
  },

  async update(pageId: string, data: UpdatePageRequest): Promise<Page> {
    const response = await apiClient.put<Page>(`/pages/${pageId}`, data);
    return response.data;
  },

  async delete(pageId: string): Promise<void> {
    await apiClient.delete(`/pages/${pageId}`);
  },

  async move(pageId: string, newPosition: number): Promise<void> {
    await apiClient.put(`/pages/${pageId}/move`, { newPosition });
  },

  async reorderPages(
    siteId: string,
    pageOrders: Array<{ pageId: string; orderPosition: number }>
  ): Promise<void> {
    await apiClient.put(`/sites/${siteId}/pages/reorder`, { pageOrders });
  },

  // Page Template methods
  async saveAsTemplate(
    pageId: string,
    data: CreatePageTemplateRequest
  ): Promise<{
    message: string;
    template: {
      id: string;
      name: string;
      description?: string;
      tags?: string;
      isPublic: boolean;
      usageCount: number;
      createdAt: string;
    };
  }> {
    // Include pageId in the request body as the backend expects it
    const requestBody = {
      ...data,
      pageId, // Add pageId to the request body
    };

    const response = await apiClient.post<{
      message: string;
      template: {
        id: string;
        name: string;
        description?: string;
        tags?: string;
        isPublic: boolean;
        usageCount: number;
        createdAt: string;
      };
    }>(`/pages/${pageId}/save-template`, requestBody);
    return response.data;
  },

  async createFromTemplate(
    siteId: string,
    data: CreatePageFromTemplateRequest
  ): Promise<CreatePageFromTemplateResponse> {
    const response = await apiClient.post<CreatePageFromTemplateResponse>(
      `/sites/${siteId}/pages/from-template`,
      data
    );
    return response.data;
  },

  async getPageHtml(pageId: string): Promise<{
    pageId: string;
    pageTitle: string;
    pageSlug: string;
    siteId: string;
    siteName: string;
    html: string;
  }> {
    const response = await apiClient.get<{
      pageId: string;
      pageTitle: string;
      pageSlug: string;
      siteId: string;
      siteName: string;
      html: string;
    }>(`/pages/${pageId}/html`);
    return response.data;
  },

  /** Publish a page - pre-renders HTML and makes it live */
  async publish(pageId: string): Promise<{ message: string; page: Page }> {
    const response = await apiClient.put<{ message: string; page: Page }>(
      `/pages/${pageId}/publish`
    );
    return response.data;
  },

  /** Unpublish a page - removes it from the live site */
  async unpublish(pageId: string): Promise<{ message: string; page: Page }> {
    const response = await apiClient.put<{ message: string; page: Page }>(
      `/pages/${pageId}/unpublish`
    );
    return response.data;
  },
};
